<template>
    <v-row justify="center">
        <v-col cols=11 lg=10 xl=6>
            <v-row dense justify="center" align="center">
                <PageTitle text="Anton Gulenko, PhD" />
                <div class="pr-sm-3" :class="desktop ? [] : [ bigMobile ? 'col-sm-6' : 'col-10' ]">
                    <v-img src="/profile.jpg" class="image-border" :class="{'centered-block': !bigMobile}" alt="Resume picture" max-width=400 />
                </div>
                <div class="d-flex align-center justify-center justify-sm-start pl-sm-3 pl-md-8 pr-0" :class="{'col-12': !bigMobile}">
                    <div>
                        <div class="mt-2" v-for="item in contacts" :key="item.link">
                            <a :href="item.link">
                                <v-row class="flex-nowrap" align="center">
                                    <v-icon color="contactIcon" :size="desktop ? 58 : 45">{{ item.icon }}</v-icon>
                                    <span class="ml-2">{{ item.text }}</span>
                                </v-row>
                            </a>
                        </div>
                    </div>
                </div>

                <SectionTitle name="Experience" />

                <v-col cols=12>
                    <v-card outlined>
                        <v-card-title :class="classes.card_header">Tech. PO & Platform Engineer<span :class="classes.date_box">12/2023 ‒ Present</span></v-card-title>

                        <v-card-subtitle>At <a href="https://www.bbraun-nv.com/">B. Braun New Ventures</a></v-card-subtitle>
                        <v-card-text>
                            <p>
                                <span :class="classes.p_header">Project:</span>
                                <ul :class="classes.text">
                                    <li>System for secure and scalable cloud connectivity for medical IoT devices</li>
                                    <li>Landscape of interconnected web applications and microservices hosted in Kubernetes</li>
                                    <li>Features include device identity, registration and authentication, telemetry, over-the-air software updates, configuration</li>
                                </ul>
                            </p>
                            <p>
                                <span :class="classes.p_header">Personal Tasks:</span>
                                <ul :class="classes.text">
                                    <li>Leading the <strong>technical product ownership and architecture</strong> of the cloud connectivity solution for medical IoT devices</li>
                                    <li>Catering to the needs of both legacy products and greenfield developments in an enterprise environment with strict regulatory requirements</li>
                                    <li>Fostering <strong>constructive communication</strong> by establishing a <strong>stakeholder community</strong> across all divisions of the B. Braun enterprise involved with medical device cloud connectivity</li>
                                    <li>As platform engineer, designed and implemented the underlying cloud platform based on <strong>Terraform, Microsoft Azure</strong>, and <strong>Kubernetes</strong>. Established basic <strong>SRE practices</strong> and <strong>Github Actions</strong> workflows with a focus on traceability</li>
                                    <li>As <strong>topic lead</strong>, managing <strong>Go</strong> developers and cloud engineers in cross-functional teams in a matrix organization</li>
                                </ul>
                            </p>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols=12>
                    <v-card outlined>
                        <v-card-title :class="classes.card_header">Senior Cloud Platform Engineer<span :class="classes.date_box">02/2023 ‒ 10/2023</span></v-card-title>

                        <v-card-subtitle>At <a href="https://realworld-one.com/">Realworld One</a></v-card-subtitle>
                        <v-card-text>
                            <p>
                                <span :class="classes.p_header">Project:</span>
                                <ul :class="classes.text">
                                    <li>Cloud platform for virtual reality content delivery</li>
                                </ul>
                            </p>
                            <p>
                                <span :class="classes.p_header">Personal Tasks:</span>
                                <ul :class="classes.text">
                                    <li>Designed and implemented full monitoring and alerting system for <strong>Kubernetes</strong>-based microservice platform</li>
                                    <li>Prepared and orchestrated full <strong>Kubernetes</strong> cluster migration with minimal downtime</li>
                                    <li>Main technologies: <strong>Terraform, Go, Microsoft Azure, Kubernetes, Prometheus, Grafana, Loki, Shell scripts</strong></li>
                                </ul>
                            </p>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols=12>
                    <v-card outlined>
                        <v-card-title :class="classes.card_header">Co-Founder & CTO<span :class="classes.date_box">04/2022 ‒ 01/2023</span></v-card-title>

                        <v-card-subtitle>At <a href="https://quantaleap.eu/">Quantaleap</a></v-card-subtitle>
                        <v-card-text>
                            <p>
                                <ul>
                                <li>Quantaleap's vision is to revolutionize operations in the cloud by leveraging data analysis and AI.</li>
                                <li>Architected and implemented MVP of workflow automation platform for cloud operations, including cloud infrastructure, CI/CD pipelines, backend services, and web frontend.</li>
                                <li>Established technological and organizational foundation for a high-tech cloud startup based in Utrecht, NL.</li>
                                </ul>
                            </p>
                            <p>
                                <span :class="classes.p_header">Main technologies:</span>
                                <ul>
                                    <li>Secure and scalable public cloud infrastructure on <strong>Microsoft Azure</strong> and <strong>Kubernetes</strong></li>
                                    <li>Automated integration and deployment, fully covered by infrastructure as code</li>
                                    <li>Web app and REST API development with <strong>TypeScript</strong> and <strong>React JS</strong></li>
                                    <li>Backend microservice development in <strong>Go</strong></li>
                                </ul>
                            </p>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols=12>
                    <v-card outlined>
                        <v-card-title :class="classes.card_header">Platform engineer & Go developer<span :class="classes.date_box">01/2021 ‒ 03/2022</span></v-card-title>

                        <v-card-subtitle>At <a href="https://www.mercedes-benz-techinnovation.com/en/">Mercedes-Benz Tech Innovation</a></v-card-subtitle>
                        <v-card-text>
                            <p>
                                <span :class="classes.p_header">Project:</span>
                                <ul :class="classes.text">
                                    <li>Internal Container-as-a-Service platform hosting over 800 Kubernetes clusters worldwide, built mainly from open source software with custom components and optimizations</li>
                                    <li>Enterprise development ecosystem with multiple teams, following a Scrum-inspired agile development workflow</li>
                                    <li>Full DevOps workflow ("You build it, you run it"), including on-call duty</li>
                                    <li>Fully automatic continuous integration and deployment</li>
                                </ul>
                            </p>
                            <p>
                                <span :class="classes.p_header">Personal Tasks:</span>
                                <ul :class="classes.text">
                                    <li>Design, coordination, and development of a global-scale monitoring system for the multi-region <strong>Kubernetes</strong> platform based on <strong><a href="https://prometheus.io/">Prometheus</a></strong> and <strong><a href="https://thanos.io/">Thanos</a></strong></li>
                                    <li>Built <strong>Go</strong> service for remediation of inconsistencies in customer-, billing-, and cluster-data</li>
                                    <li>Implemented web-based console for simple administration of customer clusters</li>
                                    <li>Improved and extended <strong>Helm</strong> charts with <strong>Kustomize</strong> overlays, <strong>Ansible</strong> playbooks, and <strong>Bazel</strong> build scripts</li>
                                </ul>
                            </p>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols=12>
                    <v-card outlined>
                        <v-card-title :class="classes.card_header">Research associate<span :class="classes.date_box">01/2015 ‒ 08/2020</span></v-card-title>

                        <v-card-subtitle>At <a href="https://www.dos.tu-berlin.de/menue/distributed_and_operating_systems/">Distributed and Operating Systems Group</a>, Technical University Berlin</v-card-subtitle>
                        <v-card-text>
                            <p>
                                <span :class="classes.p_header">Software Development & Infrastructure:</span>
                                <ul :class="classes.text">
                                    <li>Architecture and development of data stream processing framework Bitflow, based on <strong>Kubernetes</strong>, including high-performance data collection agent, DSL for dataflow graphs and Kubernetes operator (<strong>Go</strong>, <strong>Java</strong>, <strong>Python</strong>)</li>
                                    <li>Deployment and maintenance of dozens of testbed infrastructures on up to 100 physical nodes with <strong>PXE boot</strong> and <strong>Foreman</strong></li>
                                    <li>Private cloud infrastructures powered by <strong>OpenStack</strong> and <strong>Kubernetes</strong></li>
                                    <li>Automation using <strong>Ansible, Kolla, Kubespray, Heat</strong>, and <strong>Helm</strong></li>
                                    <li>Operation of software-defined networks (SDN) with <strong>OpenDaylight, Open vSwitch</strong>, and <strong>eBPF</strong></li>
                                </ul>
                            </p>
                            <p>
                                <span :class="classes.p_header">Projects with industry partners:</span>
                                <ul :class="classes.text">
                                    <li>Self-healing in cloud platforms with cross-layer monitoring and real-time data analysis: Anomaly detection, root cause classification and selection of remediation actions</li>
                                    <li>Real-time analysis of IoT data, alerting of malfunctions, proactive maintenance</li>
                                    <li>Data-driven optimization of parameters for the production process of electronic parts</li>
                                </ul>
                            </p>
                            <p>
                                <span :class="classes.p_header">Research and teaching:</span>
                                <ul :class="classes.text">
                                    <li>Numerous <a href="/publications">publications</a> on the topics of cloud architecture, monitoring, and data analysis</li>
                                    <li>Supervised numerous bachelor's and master's theses in topics related to my research and dissertation</li>
                                    <li>Designed, presented and graded practical programming excercises and written exams</li>
                                    <li>Taught courses: Cloud Computing, Distributed Systems, Operating Complex IT-Systems</li>
                                </ul>
                            </p>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols=12>
                    <v-card outlined>
                        <v-card-title :class="classes.card_header">Student employee & engineering intern<span :class="classes.date_box">10/2012 ‒ 10/2013</span></v-card-title>
                        <v-card-subtitle>At <a href="https://www.sap.com/about/sap-labs-silicon-valley.html">SAP Palo Alto</a></v-card-subtitle>
                        <v-card-text>
                            <ul :class="classes.text">
                                <li>Proof-of-concept system for analysing data of online games</li>
                                <li>Developed complex <strong>SQL</strong> queries to extract ML training and test data from large volumes of game event logs</li>
                                <li>Developed prototype system in <strong>Java</strong> to analyze big data of online games targeting to increase conversion rate, revenue and reduce churn behavior</li>
                            </ul>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols=12>
                    <v-card outlined>
                        <v-card-title :class="classes.card_header">Software developer<span :class="classes.date_box">12/2006 ‒ 04/2011</span></v-card-title>
                        <v-card-subtitle>At <a href="https://www.4flow.com/about-4flow/facts.html">4flow</a> and <a href="https://www.optimal-systems.de/en/about-us">Optimal Systems</a></v-card-subtitle>
                        <v-card-text>
                            <ul :class="classes.text">
                                <li>Developed a <strong>Java</strong>-based graphical tool for creating and executing test suites</li>
                                <li>Contributed to the user interface, database storage, network communication and business logic of Java-based logistics software</li>
                            </ul>
                        </v-card-text>
                    </v-card>
                </v-col>

                <SectionTitle name="Education" />
                <v-col cols=12>
                    <v-card outlined>
                        <v-card-title :class="classes.card_header">Ph.D. (Dr.-Ing.)<span :class="classes.date_box">01/2015 ‒ 08/2020</span></v-card-title>
                        <v-card-subtitle><a href="https://www.dos.tu-berlin.de/menue/distributed_and_operating_systems/">Distributed and Operating Systems Group</a>, Technical University Berlin</v-card-subtitle>
                        <v-card-text>
                            <ul :class="classes.text">
                                <li>Grade: Cum laude</li>
                                <li>Specialization: Reliable distributed systems</li>
                                <li>Thesis: <a href="https://depositonce.tu-berlin.de/bitstream/11303/11458/5/gulenko_anton.pdf">Autonomic Self-healing in Cloud Computing Platforms</a></li>
                            </ul>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols=12>
                    <v-card outlined>
                        <v-card-title :class="classes.card_header">Master of Science<span :class="classes.date_box">10/2011 ‒ 01/2015</span></v-card-title>
                        <v-card-subtitle>IT-Systems Engineering, Hasso Plattner Insitute</v-card-subtitle>
                        <v-card-text>
                            <ul :class="classes.text">
                                <li>Grade: 1.2 (A)</li>
                                <li>Specialization: Execution environments and optimization</li>
                                <li>Thesis: <a href="/gulenko_master_thesis.pdf">Allocation Removal in Modern Object Oriented VMs</a></li>
                                <li>Publication: <a href="https://dl.acm.org/doi/pdf/10.1145/2816707.2816716">Language-Independent Storage Strategies for Tracing-JIT-Based Virtual Machines</a></li>
                            </ul>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols=12>
                    <v-card outlined>
                        <v-card-title :class="classes.card_header">Bachelor of Science<span :class="classes.date_box">10/2008 ‒ 07/2011</span></v-card-title>
                        <v-card-subtitle>IT-Systems Engineering, Hasso Plattner Insitute</v-card-subtitle>
                        <v-card-text>
                            <ul :class="classes.text">
                                <li>Grade: 1.6 (A-)</li>
                                <li>Specialization: Software engineering and collaboration</li>
                                <li>Thesis: <a href="/gulenko_bachelor_thesis.pdf">Integrating JavaScript Language Features into Smalltalk</a></li>
                            </ul>
                        </v-card-text>
                    </v-card>
                </v-col>

                <SectionTitle name="Skills" />

                <v-col cols=12 md=6 class="d-flex" align-self="stretch" v-for="skill in skills" :key="skill.skill">
                    <v-card class="flex-grow-1" outlined>
                        <v-card-title :class="classes.card_header">{{ skill.skill }}</v-card-title>
                        <v-card-subtitle>
                            <ul :class="classes.text">
                                <li v-for="part in skill.parts" :key="part">{{ part }}</li>
                            </ul>
                        </v-card-subtitle>
                    </v-card>
                </v-col>
                <v-col cols=12>
                    <v-card outlined>
                        <v-card-title :class="classes.card_header">Languages</v-card-title>
                        <v-card-subtitle >
                            <v-row no-gutters justify="space-between" align="center">
                                <v-card class="col-6 col-sm-3" flat v-for="lang in languages" :key="lang.lang">
                                    <v-card-title class="text-h6">{{ lang.lang }}</v-card-title>
                                    <v-card-subtitle :class="classes.language">{{ lang.prof }}</v-card-subtitle>
                                </v-card>
                            </v-row>
                        </v-card-subtitle>
                    </v-card>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import SectionTitle from "./SectionTitle"
import PageTitle from "./PageTitle"

export default {
    name: "Resume",
    components: { SectionTitle, PageTitle },
    computed: {
      bigMobile: function() { return this.$vuetify.breakpoint.smAndUp },
      desktop: function() { return this.$vuetify.breakpoint.mdAndUp },
    },
    data() {
        return {
            "classes": {
                "text": "text-body-1",
                "card_header": "text-left text-h5 d-flex",
                "p_header": "text-subtitle-2",
                "language": "text-body-2",
                "date_box": "text-body-2 pa-1 ml-auto",
            },
            "contacts": [
                { link: "https://antongulenko.info", text: "antongulenko.info", icon: "mdi-web" },
                { link: "https://www.linkedin.com/in/antongulenko", text: "antongulenko", icon: "mdi-linkedin" },
                { link: "https://github.com/antongulenko", text: "antongulenko", icon: "mdi-github" },
                { link: "mailto:anton.gulenko@gmail.com", text: "anton.gulenko@gmail.com", icon: "mdi-at" },
                { link: "tel:+4915253452132", text: "+49 152 53452132", icon: "mdi-phone" },
            ],
            "skills": [
                { skill: "DevOps & Infrastructure", parts: [
                    "CI/CD: Github Actions, Microsoft Azure DevOps",
                    "Infrastructure as code: Terraform, Ansible, Helm, GitOps",
                    "Linux server: Debian, Shell & Scripting, SSH"
                ]},
                { skill: "Cloud & Container", parts: [
                    "Container: Kubernetes, Docker, Docker Swarm, Kubespray",
                    "Public Clouds: Microsoft Azure, Google Cloud Platform, Amazon AWS",
                    "Private Clouds: OpenStack"
                ]},
                { skill: "Programming", parts: [
                    "Compiled languages: Go, C",
                    "Object-oriented programming: Java, Python, Smalltalk",
                    "Web Development: TypeScript, Javascript, Node, React, VueJS, HTML, CSS"
                ]},
                { skill: "Certifications", parts: [
                    "Microsoft Azure: Administrator Associate (04/2022)"
                ]}
            ],
            "languages": [
                { lang: "German", prof: "Native speaker" },
                { lang: "Russian", prof: "Native speaker" },
                { lang: "English", prof: "Full working proficiency" },
                { lang: "French", prof: "Limited working proficiency" },
            ]
        }
    }
}
</script>

<style scoped>
</style>
